import { useCallback, useEffect, useRef } from 'react';

/**
 * Guarda del RETROCESO del historial mientras la conversación está montada.
 *
 * Problema: la X pasa por `requestLeave` (aviso de charla corta / debrief /
 * vuelta al player), pero el back del browser, el gesto de atrás y el botón
 * físico de Android (que `useNativeShell` traduce a `history.back()`) hacían
 * que react-router desmontara la pantalla en seco: la sesión cerraba bien (el
 * unmount manda el `end` deliberado), pero sin aviso, sin debrief y sin el
 * `markConversed` de Escuchar. Con `<BrowserRouter>` no hay `useBlocker` (es del
 * data router) y `popstate` no se puede cancelar — la única forma de absorber
 * un back es que haya una entrada DE MÁS para popear.
 *
 * Cómo: al montar apila una entrada CENTINELA (misma URL, `history.state`
 * marcado con `SENTINEL_FLAG`). Un back popea el centinela y cae en la entrada
 * real de la conversación (misma ruta → react-router no desmonta nada); el
 * listener de `popstate` repone el centinela y dispara `onBack` — el mismo
 * camino que la X.
 *
 * Contrato de salida: mientras el centinela esté arriba, la conversación ocupa
 * DOS entradas. Toda navegación que abandona la pantalla pasa por `exit(then)`:
 * popea el centinela y recién cuando ese pop aterriza corre `then`, así la
 * entrada real queda como única huella y `then` la REEMPLAZA (`replace: true`)
 * o la popea (`navigate(-1)` en Escuchar). Una navegación directa dejaría el
 * centinela huérfano: al volver a esa pantalla un back "no haría nada" una vez.
 *
 * Idempotente ante el doble mount de StrictMode y el remount por epoch de
 * `ConversationScreen` (si el centinela ya está arriba, no apila otro) y ante un
 * reload con el centinela arriba (react-router lee `usr`/`key` del state, que el
 * centinela copia de la entrada real).
 */
export interface HistoryBackGuard {
  /**
   * Salida ordenada de la pantalla: saca el centinela del historial y recién
   * después corre `then` (la navegación real). Un segundo `exit` mientras el
   * primero está en vuelo se ignora (doble tap). Sin centinela arriba (guarda
   * desarmada o entrada ya reemplazada) corre `then` en seco.
   */
  exit: (then: () => void) => void;
}

/** Marca en `history.state` de la entrada centinela (la que absorbe el back). */
const SENTINEL_FLAG = '__orborixBackGuard';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSentinel(state: unknown): boolean {
  return isRecord(state) && state[SENTINEL_FLAG] === true;
}

/**
 * Apila el centinela copiando el state de la entrada real (react-router guarda
 * ahí `usr`/`key`/`idx`): si el browser recarga con el centinela arriba, el
 * router reconstruye la misma location.
 */
function pushSentinel(): void {
  const base = isRecord(window.history.state) ? window.history.state : {};
  window.history.pushState({ ...base, [SENTINEL_FLAG]: true }, '', window.location.href);
}

export function useHistoryBackGuard(onBack: () => void): HistoryBackGuard {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const armedRef = useRef(false);
  /** Navegación diferida por `exit()`: corre cuando el pop del centinela aterriza. */
  const pendingExitRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isSentinel(window.history.state)) pushSentinel();
    armedRef.current = true;

    const onPop = (): void => {
      const pending = pendingExitRef.current;
      if (pending) {
        // Pop del centinela pedido por `exit()`: ahora sí, la navegación real.
        pendingExitRef.current = null;
        armedRef.current = false;
        pending();
        return;
      }
      // Aterrizar EN un centinela (forward tras salir) no es un back del user.
      if (!armedRef.current || isSentinel(window.history.state)) return;
      // Back del user: el centinela absorbió el pop. Lo reponemos y desviamos
      // por el mismo camino que la X.
      pushSentinel();
      onBackRef.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      armedRef.current = false;
    };
  }, []);

  const exit = useCallback((then: () => void): void => {
    if (pendingExitRef.current) return;
    if (!armedRef.current || !isSentinel(window.history.state)) {
      then();
      return;
    }
    pendingExitRef.current = then;
    window.history.back();
  }, []);

  return { exit };
}
